import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Banner from '../components/Banner';
import Breadcrumb from '../components/Breadcrumb';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

import staffingImg from "../images/services/it-staff.jpg";
import trainingImg from "../images/services/training-program.jpg";
import careerImg from "../images/services/career-dev.jpg";
import cloudImg from "../images/services/cloud-solutions.jpg";
import ServiceBg from '../images/service-bg.png';

import '../styles/pages/ServiceDetails.css';

import { FaCheckCircle, FaUserTie, FaLaptopCode, FaCloud, FaGraduationCap, FaAngleRight } from "react-icons/fa";

const ServiceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Scroll to top on id change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    const serviceData = {
        "it-staffing": {
            title: "IT Staffing Services",
            image: staffingImg,
            shortDesc: "We provide top-tier IT professionals to support your business growth:",
            content: "Fortune Cloud Solutions specializes in connecting businesses with elite technical talent across specialized domains. We provide high-caliber experts including Software Developers (Java, .NET, Python, Full Stack), Cloud Engineers (AWS, Azure, GCP), DevOps & Automation Experts, Data Platform Specialists, and QA & Testing Professionals. Our mission is to bridge the talent gap with precision and speed, ensuring your technical projects are powered by the best in the industry.",
            benefits: [
                "Contract, Full-time, and Remote staffing models tailored to your needs.",
                "Fast and reliable hiring process ensuring minimal project downtime.",
                "Access to an extensive Global talent pool of vetted IT professionals.",
                "Rigorous technical screening to ensure perfect skill alignment."
            ],
            icon: <FaUserTie className="hero-service-icon" />
        },
        "training-programs": {
            title: "Student Training Programs",
            image: trainingImg,
            shortDesc: "We prepare students and job seekers for real-world careers:",
            content: "Knowledge without application is the missing link in modern education. Our comprehensive programs focus on creating industry-ready professionals through hands-on IT training with live projects, career coaching, resume building, and intensive interview preparation. We also prioritize holistic development through communication and personality development sessions, culminating in dedicated placement assistance to ensure your transition into a professional IT career is seamless and successful.",
            benefits: [
                "Industry-relevant curriculum updated with current technology trends.",
                "A practical learning approach emphasizing 'learning-by-doing'.",
                "Career-focused training designed to meet specific employer demands.",
                "Real-world project simulations for valuable technical exposure."
            ],
            icon: <FaLaptopCode className="hero-service-icon" />
        },
        "career-development": {
            title: "Career Development",
            image: careerImg,
            shortDesc: "Guiding individuals to achieve their career goals in the IT industry.",
            content: "We are committed to fostering professional excellence through our comprehensive Career Development initiatives. Navigating the complex terrain of the IT industry requires strategic guidance, continuous mentorship, and personalized roadmap planning. We provide aspiring and seasoned professionals with the tools, insights, and networks necessary to overcome career plateaus and achieve their maximal potential in high-demand technical roles.",
            benefits: [
                "Personalized career mapping and one-on-one professional mentorship.",
                "Professional resume optimization and targeted interview preparation.",
                "Access to exclusive industry networking events and strategic alumni circles.",
                "Ongoing career counseling tailored to evolving market demands."
            ],
            icon: <FaGraduationCap className="hero-service-icon" />
        },
        "cloud-solutions": {
            title: "Cloud Solutions",
            image: cloudImg,
            shortDesc: "Providing scalable and secure cloud solutions to help businesses improve efficiency and digital transformation.",
            content: "Our Cloud Solutions are engineered to drive unprecedented efficiency, agility, and robust digital transformation for modern enterprises. As organizations pivot towards decentralized and scalable infrastructures, we offer end-to-end cloud engineering services. From initial strategic consulting and secure migration architectures to continuous DevOps integration and resilient cloud management, we empower your business to operate seamlessly in AWS, Azure, or Google Cloud environments.",
            benefits: [
                "Accelerated digital transformation enabling highly scalable infrastructure.",
                "Robust enterprise-grade security protocols and compliance frameworks.",
                "Optimized operational costs through efficient cloud resource management.",
                "Comprehensive DevOps enablement and continuous integration pipelines."
            ],
            icon: <FaCloud className="hero-service-icon" />
        }
    };

    const currentService = serviceData[id];

    // If route doesn't match any service, handle gracefully
    if (!currentService) {
        return (
            <div className="container py-5 text-center" style={{ minHeight: "60vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <h2>Service Not Found</h2>
                <p>The service you are looking for does not exist.</p>
                <button className="btn btn-custom-purple mt-3" onClick={() => navigate('/services')}>Back to Services</button>
            </div>
        );
    }

    const allServicesLinks = [
        { path: 'it-staffing', name: 'IT Staffing' },
        { path: 'training-programs', name: 'Training Programs' },
        { path: 'career-development', name: 'Career Development' },
        { path: 'cloud-solutions', name: 'Cloud Solutions' },
    ];

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <SEO title={`${currentService.title} - Services`} />

            <Banner title={currentService.title} image={ServiceBg} />

            <div className="container py-4">
                <Breadcrumb customPath="Services / Details" />
            </div>

            <section className="service-details-section py-5">
                <div className="container">
                    <div className="row">

                        {/* LEFT CONTENT */}
                        <motion.div
                            className="col-lg-8"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                        >
                            <div className="service-detail-image mb-4 position-relative">
                                <img src={currentService.image} alt={currentService.title} className="img-fluid rounded shadow-sm w-100" />
                                <div className="service-icon-badge shadow">
                                    {currentService.icon}
                                </div>
                            </div>

                            <h2 className="mb-3 text-uppercase fw-bold text-dark">{currentService.title}</h2>
                            <h5 className="text-secondary mb-4 fst-italic border-start border-custom-purple border-4 ps-3 py-2 bg-light">
                                {currentService.shortDesc}
                            </h5>

                            <p className="description-text mb-4 text-muted">
                                {currentService.content}
                            </p>

                            <div className="benefits-wrapper mt-5 p-4 bg-light rounded shadow-sm">
                                <h4 className="mb-4 fw-bold">Key Benefits & Features</h4>
                                <ul className="list-unstyled benefits-list">
                                    {currentService.benefits.map((benefit, idx) => (
                                        <motion.li
                                            key={idx}
                                            className="d-flex align-items-start mb-3"
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.1 }}
                                            viewport={{ once: true }}
                                        >
                                            <FaCheckCircle className="text-custom-purple mt-1 me-3 fs-5 flex-shrink-0" />
                                            <span className="text-dark fw-medium">{benefit}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>

                        {/* RIGHT SIDEBAR */}
                        <motion.div
                            className="col-lg-4 mt-5 mt-lg-0"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                        >
                            <div className="service-sidebar p-4 bg-white rounded shadow-sm sticky-top" style={{ top: "100px", zIndex: 10 }}>
                                <h4 className="mb-4 text-uppercase border-bottom pb-2 fw-bold">Other Services</h4>
                                <ul className="list-unstyled sidebar-services-list">
                                    {allServicesLinks.map((link, idx) => (
                                        <li key={idx} className="mb-3">
                                            <Link
                                                to={`/services/${link.path}`}
                                                className={`d-flex align-items-center justify-content-between p-3 rounded text-decoration-none transition-all ${id === link.path ? 'active-service text-white bg-custom-purple' : 'bg-light text-dark hover-service'}`}
                                            >
                                                <span className="fw-semibold">{link.name}</span>
                                                <FaAngleRight />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>

                                <div className="sidebar-contact mt-5 bg-dark text-white p-4 rounded text-center position-relative overflow-hidden">
                                    <div className="contact-overlay"></div>
                                    <div className="position-relative z-1">
                                        <h5 className="text-uppercase mb-3 fw-bold text-custom-purple">Need Help?</h5>
                                        <p className="small mb-4 text-light">Reach out to us to find the perfect solution tailored specifically to your organizational demands.</p>
                                        <Link to="/contact">
                                            <button className="btn btn-outline-light w-100 rounded-pill py-2 fw-bold text-uppercase">Contact Us</button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>
        </motion.div>
    );
};

export default ServiceDetails;
